
import React, { useState, useLayoutEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Check, Sparkles } from 'lucide-react';

interface Step {
  targetId: string;
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'center';
}

const STEPS: Step[] = [
  {
    targetId: 'tour-summary',
    title: 'Visão Geral',
    content: 'Aqui você acompanha o progresso total da sua obra e quanto do seu orçamento já foi utilizado.',
    position: 'bottom'
  },
  {
    targetId: 'tour-add-expense',
    title: 'Lançamento Rápido',
    content: 'Sempre que comprar algo para a obra, clique aqui para registrar. É simples e rápido!',
    position: 'top'
  },
  {
    targetId: 'tour-tab-photos',
    title: 'Diário de Fotos',
    content: 'Registre a evolução visual da sua casa. Nada de fotos perdidas no WhatsApp.',
    position: 'top'
  },
  {
    targetId: 'tour-tab-stats',
    title: 'Relatórios e PDFs',
    content: 'Gere documentos profissionais com um clique para enviar ao banco ou para o casal avaliar os custos.',
    position: 'top'
  }
];

export const OnboardingTutorial: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const updateRect = useCallback(() => {
    const el = document.getElementById(STEPS[currentStep].targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      // Pequeno delay para garantir que o scroll terminou antes de calcular a posição
      setTimeout(() => {
        if (el) setTargetRect(el.getBoundingClientRect());
      }, 400);
    } else {
      // Se elemento não existe na tela (ex: mobile vs desktop), pula
      handleNext();
    }
  }, [currentStep]);

  useLayoutEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, { passive: true });
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect);
    };
  }, [updateRect]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      finishTour();
    }
  };

  const finishTour = () => {
    localStorage.setItem('tutorial_completed', 'true');
    onComplete();
  };

  if (!targetRect) return null;

  const step = STEPS[currentStep];
  
  const getBalloonStyle = () => {
    const isMobile = window.innerWidth < 640;
    const balloonWidth = isMobile ? Math.min(window.innerWidth * 0.9, 340) : 340;
    const margin = 20;

    const style: React.CSSProperties = {
      position: 'fixed',
      left: '50%',
      transform: 'translateX(-50%)',
      width: balloonWidth,
      zIndex: 1010,
    };

    const spaceTop = targetRect.top;
    const spaceBottom = window.innerHeight - targetRect.bottom;

    // Lógica para decidir se mostra em cima ou embaixo com margem de segurança
    if (spaceBottom > 280 || spaceBottom > spaceTop) {
        style.top = Math.min(targetRect.bottom + margin, window.innerHeight - 300);
    } else {
        style.bottom = Math.min(window.innerHeight - targetRect.top + margin, window.innerHeight - 300);
    }

    return style;
  };

  return (
    <div className="fixed inset-0 z-[1000] pointer-events-auto">
      
      {/* Botão FECHAR GLOBAL FIXO - Para destravar o usuário */}
      <button 
        onClick={finishTour} 
        className="fixed top-6 right-6 z-[1020] p-3 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full text-white border border-white/20 shadow-xl transition-all active:scale-95"
      >
          <X size={24} />
      </button>

      {/* SVG Mask Background */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="hole">
            <rect width="100%" height="100%" fill="white" />
            <rect 
              x={targetRect.left - 8} 
              y={targetRect.top - 8} 
              width={targetRect.width + 16} 
              height={targetRect.height + 16} 
              rx="16" 
              fill="black" 
            />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(2, 6, 23, 0.7)" mask="url(#hole)" />
      </svg>

      {/* Balão de Dica */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-emerald-500/20"
          style={getBalloonStyle()}
        >
          <div className="flex items-center gap-2 mb-3">
             <div className="size-6 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center text-emerald-600">
                <Sparkles size={14} fill="currentColor" />
             </div>
             <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Dica do Mestre</h4>
          </div>
          
          <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2 leading-tight uppercase tracking-tight">{step.title}</h3>
          <p className="text-sm font-bold text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
            {step.content}
          </p>

          <div className="flex items-center justify-end gap-3">
              <span className="text-[10px] font-black text-slate-300 mr-auto">{currentStep + 1}/{STEPS.length}</span>
              <button 
                onClick={handleNext}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-600/20 flex items-center gap-2 transition-all active:scale-95"
              >
                {currentStep === STEPS.length - 1 ? (
                  <>Finalizar <Check size={14} strokeWidth={3} /></>
                ) : (
                  <>Próximo <ArrowRight size={14} /></>
                )}
              </button>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Borda Pulsante no Alvo */}
      <div 
        className="absolute z-[1005] border-2 border-emerald-400 rounded-2xl pointer-events-none transition-all duration-300 ease-out"
        style={{
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
          boxShadow: '0 0 0 4px rgba(52, 211, 153, 0.2), 0 0 30px 10px rgba(52, 211, 153, 0.3)'
        }}
      />
    </div>
  );
};
